import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import { window } from "@swa-ui/browser";
import { classNames } from "@swa-ui/string";

import { Anchor } from "../Anchor";
import { AnchorTarget } from "../AnchorTarget";
import { CaptionPointer } from "../CaptionPointer";
import { FocusContainer } from "../FocusContainer";
import { Shadow } from "../Shadow";
import { Transition } from "../Transition";
import { Width, widthPropTypes } from "../Width";
import styles from "./Flyout.module.scss";
import { PortalWrapper } from "./PortalWrapper";

const ANIMATION_DELAY_HIDDEN = 0.05;
const CAPTION_POINTER_SIZE = 24;
const DURATION_FAST = 0.1;
const DURATION_NORMAL = 0.25;
const DURATION_SLOW = 0.3;
const FILTER_MAXIMUM = "1.5px";
const FILTER_NONE = "0px";
const OFFSET_FOR_BORDERS = 2;
const OPAQUE = 1;
const POSITION_END = 0;
const POSITION_START = 2;
const ROTATION_MEDIUM = 12;
const ROTATION_NONE = 0;
const TRANSFORM_ORIGIN_CENTER = "50%";
const TRANSFORM_ORIGIN_NEAR_END = "75%";
const TRANSFORM_ORIGIN_NEAR_START = "25%";
const TRANSPARENT = 0;

/**
 * Flyout provides a way to display an element adjacent to another element ("anchor") to appear as a
 * top level "overlay". See the location propType description for an important note.
 *
 * NOTE:
 * classNameAnchor and classNameFlyout must be given.
 * classNameAnchor must contain an anchor-name property and classNameFlyout contain a position-anchor
 * property. These should be something like:
 *
 * .classNameAnchor {
 *   anchor-name: --my-anchor;
 * }
 *
 * .classNameFlyout {
 *   position-anchor: --my-anchor;
 * }
 *
 * The name given to these properties, '--my-anchor' in the example above, must match. See Anchor
 * and AnchorTarget for info.
 *
 * TODO:
 * The classNameAnchor and classNameFlyout requirements are temporary until browser support is
 * widely implemented. These two props can then go away with PHX-2817.
 */

export const Flyout = (props) => {
  const {
    alignment,
    bestFit = true,
    children,
    className,
    classNameAnchor,
    classNameFlyout,
    constrainFocus = false,
    flyout,
    id,
    location = "hidden",
    name,
    onTransformationEnd,
    pointerAlignment = "center",
    pointerXOffset = OFFSET_FOR_BORDERS,
    portal = false,
    showPointer = true,
    width,
  } = props;
  const [bestLocation, setBestLocation] = useState("hidden");
  const [currentLocation, setCurrentLocation] = useState("hidden");
  const containerRef = useRef();
  const mainContentContainer = useRef();

  useEffect(() => {
    if (portal) {
      setBestLocation(location === "hidden" ? "hidden" : "below");
    } else {
      if (bestFit && location === "above" && isAnchorAboveOfCenter()) {
        setBestLocation("below");
      } else if (bestFit && location === "below" && !isAnchorAboveOfCenter()) {
        setBestLocation("above");
      } else if (bestFit && location === "left" && isAnchorLeftOfCenter()) {
        setBestLocation("right");
      } else if (bestFit && location === "right" && !isAnchorLeftOfCenter()) {
        setBestLocation("left");
      } else {
        setBestLocation(location);
      }
    }
  }, [location]);

  return (
    <div className={getClass()} id={id} ref={containerRef}>
      <Anchor {...getAnchorProps()}>
        <div ref={mainContentContainer}>{children}</div>
      </Anchor>
      {portal ? renderPortal() : renderFlyout()}
      {portal ? renderPortalPointer() : renderPointer()}
    </div>
  );

  function renderPortal() {
    return (
      <PortalWrapper containerRef={mainContentContainer} isAboveOrBelow={isAboveOrBelow()}>
        {renderFlyout()}
      </PortalWrapper>
    );
  }

  function renderFlyout() {
    return (
      <AnchorTarget {...getTargetProps()}>
        <Transition {...getTransitionProps()}>
          <Shadow>{renderFlyoutContent()}</Shadow>
        </Transition>
      </AnchorTarget>
    );
  }

  function renderPortalPointer() {
    return (
      <PortalWrapper containerRef={mainContentContainer} isAboveOrBelow={isAboveOrBelow()}>
        {renderPointer()}
      </PortalWrapper>
    );
  }

  function renderPointer() {
    return showPointer ? (
      <AnchorTarget {...getPointerProps()}>
        <Transition {...getPointerTransitionProps()}>
          <CaptionPointer {...getCaptionPointerProps()} />
        </Transition>
      </AnchorTarget>
    ) : null;
  }

  function renderFlyoutContent() {
    let content = renderContent();

    if (constrainFocus) {
      content = <FocusContainer {...getFocusContainerProps()}>{content}</FocusContainer>;
    }

    if (width) {
      content = <Width width={width}>{content}</Width>;
    }

    return content;
  }

  function renderContent() {
    return <div {...getFlyoutContentProps()}>{flyout}</div>;
  }

  function getAnchorProps() {
    return {
      className: classNames(styles.anchor, classNameAnchor),
      name,
    };
  }

  function getFlyoutContentProps() {
    return {
      "aria-describedby": props["aria-describedby"],
      "aria-label": props["aria-label"],
      "aria-labelledby": props["aria-labelledby"],
      ...(props["aria-label"] ||
        (props["aria-labelledby"] && {
          "aria-modal": "true",
          role: "dialog",
        })),
    };
  }

  function getTransitionProps() {
    return {
      animate: getAnimationValues(),
      "aria-hidden": currentLocation === "hidden",
      className: styles.transition,
      onTransformationEnd: handleTransformationEnd,
      style: { transformOrigin: getTransformOrigin() },
      transition: getTransition(),
    };
  }

  function getFocusContainerProps() {
    return {
      "aria-hidden": props["aria-hidden"] || currentLocation === "hidden",
      revealed: currentLocation !== "hidden",
    };
  }

  function getPointerTransitionProps() {
    return {
      animate: { opacity: getOpacity() },
      transition: getPointerTransition(),
    };
  }

  function getTargetProps() {
    return {
      className: classNames(styles.target, classNameFlyout, {
        [styles.alignmentLeft]: alignment === "left",
        [styles.alignmentRight]: alignment === "right",
        [styles[bestLocation]]: showPointer && bestLocation !== "hidden",
        [styles[currentLocation]]: showPointer && currentLocation !== "hidden",
        [styles.hidden]: currentLocation === "hidden" && bestLocation === "hidden",
      }),
      location:
        (alignment === "left" || alignment === "right") && isAboveOrBelow()
          ? `${bestLocation} ${alignment}`
          : getLocation(),
      name,
    };
  }

  function getPointerProps() {
    return {
      className: classNameFlyout,
      location:
        (pointerAlignment === "left" || pointerAlignment === "right") && isAboveOrBelow()
          ? `${bestLocation} ${pointerAlignment}`
          : getLocation(),
      name,
    };
  }

  function getCaptionPointerProps() {
    const aboveOrBelow = isAboveOrBelow();

    return {
      className: classNames({
        [styles.pointerAlignmentAboveLeft]: pointerAlignment === "left" && bestLocation === "above",
        [styles.pointerAlignmentAboveRight]:
          pointerAlignment === "right" && bestLocation === "above",
        [styles.pointerAlignmentLeft]: pointerAlignment === "left" && aboveOrBelow,
        [styles.pointerAlignmentRight]: pointerAlignment === "right" && aboveOrBelow,
      }),
      direction: getPointerDirection(),
      ...(pointerAlignment === "left" && { style: { left: pointerXOffset } }),
      ...(pointerAlignment === "right" && { style: { right: pointerXOffset } }),
    };
  }

  function getClass() {
    return classNames(styles.flyout, { [styles.open]: currentLocation !== "hidden" }, className);
  }

  function handleTransformationEnd() {
    setCurrentLocation(bestLocation);
    onTransformationEnd?.();
  }

  function getAnimationValues() {
    return {
      filter: getFilter(),
      opacity: getOpacity(),
      rotateX: getXRotation(),
      rotateY: getYRotation(),
      x: getX(),
      y: getY(),
    };
  }

  function getFilter() {
    return bestLocation === "hidden"
      ? [`blur(${FILTER_NONE})`, `blur(${FILTER_MAXIMUM})`]
      : [`blur(${FILTER_MAXIMUM})`, `blur(${FILTER_NONE})`];
  }

  function getOpacity() {
    return bestLocation === "hidden" ? [OPAQUE, TRANSPARENT] : [TRANSPARENT, OPAQUE];
  }

  function getXRotation() {
    return bestLocation === "above"
      ? [ROTATION_MEDIUM, ROTATION_NONE]
      : bestLocation === "below"
      ? [-ROTATION_MEDIUM, ROTATION_NONE]
      : [ROTATION_NONE, ROTATION_NONE];
  }

  function getYRotation() {
    return bestLocation === "left"
      ? [-ROTATION_MEDIUM, ROTATION_NONE]
      : bestLocation === "right"
      ? [ROTATION_MEDIUM, ROTATION_NONE]
      : [ROTATION_NONE, ROTATION_NONE];
  }

  function getTransformOrigin() {
    const origins = {
      above: `${TRANSFORM_ORIGIN_NEAR_END} ${TRANSFORM_ORIGIN_CENTER}`,
      below: `${TRANSFORM_ORIGIN_NEAR_START} ${TRANSFORM_ORIGIN_CENTER}`,
      hidden: `${TRANSFORM_ORIGIN_CENTER} ${TRANSFORM_ORIGIN_CENTER}`,
      left: `${TRANSFORM_ORIGIN_NEAR_END} ${TRANSFORM_ORIGIN_CENTER}`,
      right: `${TRANSFORM_ORIGIN_NEAR_START} ${TRANSFORM_ORIGIN_CENTER}`,
    };

    return origins[bestLocation];
  }

  function getX() {
    return bestLocation === "left"
      ? [POSITION_START, POSITION_END]
      : bestLocation === "right"
      ? [-POSITION_START, POSITION_END]
      : [POSITION_END, POSITION_END];
  }

  function getY() {
    return bestLocation === "above"
      ? [POSITION_START, POSITION_END]
      : bestLocation === "below"
      ? [-POSITION_START, POSITION_END]
      : [POSITION_END, POSITION_END];
  }

  function getTransition() {
    return {
      duration: bestLocation === "hidden" ? DURATION_FAST : DURATION_NORMAL,
      ease: "easeIn",
    };
  }

  function getPointerTransition() {
    return {
      delay: bestLocation === "hidden" ? 0 : ANIMATION_DELAY_HIDDEN,
      duration: bestLocation === "hidden" ? DURATION_FAST : DURATION_SLOW,
      ease: "easeOut",
    };
  }

  function getPointerDirection() {
    const directions = {
      above: "down",
      below: "up",
      left: "right",
      right: "left",
    };

    return directions[getLocation()];
  }

  function isAboveOrBelow() {
    const place = getLocation();

    return place === "above" || place === "below";
  }

  function isAnchorAboveOfCenter() {
    return (
      containerRef.current?.getBoundingClientRect().y < window.innerHeight / 2 + window.scrollY
    );
  }

  function isAnchorLeftOfCenter() {
    return containerRef.current?.getBoundingClientRect().x < window.innerWidth / 2 + window.scrollX;
  }

  function getLocation() {
    return bestLocation === "hidden" ? currentLocation : bestLocation;
  }
};

export const flyoutPropTypes = {
  /** Where target flyout will be placed. */
  alignment: PropTypes.oneOf(["center", "left", "right"]),

  /** aria-describedby text to provide accessibility description for the flyout. */
  "aria-describedby": PropTypes.string,

  /** aria-label text to provide accessibility description for the flyout. */
  "aria-label": PropTypes.string,

  /** aria-labelledby text to provide accessibility label for the flyout. */
  "aria-labelledby": PropTypes.string,

  /**
   * Given this prop, Flyout will position the flyout in the portion of the viewport with more room.
   * That is, if the Flyout location is set to, say "above", but there's more room below the anchor,
   * Flyout will position the flyout in the "below" location.
   */
  bestFit: PropTypes.bool,

  /** Content that will be rendered. */
  children: PropTypes.node,

  /** Additional classes for positioning the component. */
  className: PropTypes.string,

  /**
   * Additional classes for positioning the component. It must contain a anchor-name property.
   * Something like:
   * anchor-name: --my-anchor;
   */
  classNameAnchor: PropTypes.string,

  /**
   * Additional classes for positioning the component. It must contain a position-anchor property.
   * Something like:
   * position-anchor: --my-anchor;
   */
  classNameFlyout: PropTypes.string,

  /**
   * Flyout is able to constrain focus to the flyout if desired. When set, the traveler, cannot set
   * focus on an element outside the flyout using tab and shift-tab.
   */
  constrainFocus: PropTypes.bool,

  /** Content to be render inside flyout when location is not "hidden". */
  flyout: PropTypes.node,

  /**
   * Location indicates which adjacent side to render target relative to anchor. When placing the
   * flyout (setting the location to anything but hidden), location must be set to hidden first. For
   * example, if the location is set to "above", then it's changed to "left", there may be a flash
   * during the rendering in the new location. Always set to hidden before repositioning to avoid
   * this flash.
   */
  location: PropTypes.oneOf(["above", "below", "hidden", "left", "right"]),

  /**
   * Name that will be used to tie Anchor and Flyout together. Both Anchor and Flyout must
   * receive the same name prop value.
   */
  name: PropTypes.string.isRequired,

  /** Option callback to inform when caption has been placed. */
  onTransformationEnd: PropTypes.func,

  /**
   * Alignment here refers to the alignment relative to the anchor (children). The pointer's
   * alignment can only be set when location is "above" or "below".
   */
  pointerAlignment: PropTypes.oneOf(["center", "left", "right"]),

  /** Positive or negative value to nudge the pointer left or right. */
  pointerXOffset: PropTypes.number,

  /**
   * Whether to render the flyout content in a React portal. NOTE: when portal is used, the location
   * will always be set to "below".
   */
  portal: PropTypes.bool,

  /** True or false to define if the caret will be displayed between children (anchor) and flyout. */
  showPointer: PropTypes.bool,

  /** Width of content container. */
  width: widthPropTypes.width,
};

Flyout.propTypes = flyoutPropTypes;
